import React from 'react';
import FormField from './FormField';

const RESIZE_CLASSES = {
  none: 'resize-none',
  vertical: 'resize-y',
  horizontal: 'resize-x',
  both: 'resize',
} as const;

export interface TextAreaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'resize'> {
  label?: string;
  error?: string;
  hint?: string;
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
  className?: string;
}

const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label,
      error,
      hint,
      resize = 'vertical',
      className = '',
      id,
      ...textareaProps
    },
    ref,
  ) => {
    const textareaId =
      id ?? (label ? `textarea-${label.replace(/\s/g, '-').toLowerCase()}` : undefined);

    const textareaClasses = [
      'w-full rounded-xl bg-surface-primary border text-content text-sm placeholder-content-muted outline-none focus:border-brand/40 transition-all',
      RESIZE_CLASSES[resize],
      error ? 'border-status-error/40' : 'border-subtle',
    ]
      .filter(Boolean)
      .join(' ');

    const textarea = (
      <textarea
        ref={ref}
        id={textareaId}
        className={`${textareaClasses} px-3 py-2.5`}
        {...textareaProps}
      />
    );

    if (label || error || hint) {
      return (
        <FormField
          label={label}
          error={error}
          hint={hint}
          required={textareaProps.required}
          htmlFor={textareaId}
          className={className}
        >
          {textarea}
        </FormField>
      );
    }

    return <div className={className}>{textarea}</div>;
  },
);

TextArea.displayName = 'TextArea';

export default TextArea;
